import React from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material'

export interface EditProfileOption {
    value: string
    label: string
}

export interface EditProfileFormValues {
    title: string
    firstName: string
    lastName: string
    gender: string
    phoneCountryCode: string
    phoneNumber: string
    email: string
    dateOfBirth: string
    screenName: string
    addressLine1: string
    city: string
    state: string
    postalCode: string
    country: string
}

interface EditProfileProps {
    values: EditProfileFormValues
    t: (key: string) => string
    titleOptions: EditProfileOption[]
    genderOptions: EditProfileOption[]
    countryCodeOptions: EditProfileOption[]
    isSaving?: boolean
    saveError?: string | null
    onChange: (patch: Partial<EditProfileFormValues>) => void
    onCancel: () => void
    onSave: () => void
}

const EditProfile: React.FC<EditProfileProps> = ({
    values,
    t,
    titleOptions,
    genderOptions,
    countryCodeOptions,
    isSaving,
    saveError,
    onChange,
    onCancel,
    onSave
}) => {
    return (
        <Box sx={{ maxWidth: 850 }}>
            <Typography
                variant="h5"
                sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    lineHeight: 1.3,
                    color: 'text.primary',
                    mb: { xs: 2, sm: 2.5 }
                }}
            >
                {t('editTitle')}
            </Typography>

            <Card elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <CardContent sx={{ p: 5 }}>
                    <Stack spacing={2} gap={1.5}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ width: { xs: '100%', sm: 140 } }}>
                                <FormControl fullWidth>
                                    <InputLabel id="edit-profile-title-label">{t('titleLabel')}</InputLabel>
                                    <Select
                                        labelId="edit-profile-title-label"
                                        value={values.title}
                                        label={t('titleLabel')}
                                        onChange={e => onChange({ title: e.target.value })}
                                    >
                                        {titleOptions.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
                                <TextField
                                    label={t('firstName')}
                                    value={values.firstName}
                                    onChange={e => onChange({ firstName: e.target.value })}
                                    fullWidth
                                />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
                                <TextField
                                    label={t('lastName')}
                                    value={values.lastName}
                                    onChange={e => onChange({ lastName: e.target.value })}
                                    fullWidth
                                />
                            </Box>
                        </Box>

                        <FormControl fullWidth>
                            <InputLabel id="edit-profile-gender-label">{t('gender')}</InputLabel>
                            <Select
                                labelId="edit-profile-gender-label"
                                value={values.gender}
                                label={t('gender')}
                                onChange={e => onChange({ gender: e.target.value })}
                            >
                                {genderOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
                                {t('mobileNumber')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                <Box sx={{ width: { xs: '100%', sm: 180 } }}>
                                    <FormControl fullWidth disabled>
                                        <InputLabel id="edit-profile-country-code-label">{t('code')}</InputLabel>
                                        <Select
                                            labelId="edit-profile-country-code-label"
                                            value={values.phoneCountryCode}
                                            label={t('code')}
                                            onChange={e => onChange({ phoneCountryCode: e.target.value })}
                                        >
                                            {countryCodeOptions.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 240 } }}>
                                    <TextField
                                        value={values.phoneNumber}
                                        onChange={e => onChange({ phoneNumber: e.target.value })}
                                        fullWidth
                                        disabled
                                    />
                                </Box>
                            </Box>
                        </Box>

                        <TextField
                            label={t('emailId')}
                            value={values.email}
                            onChange={e => onChange({ email: e.target.value })}
                            fullWidth
                            disabled
                        />

                        <TextField
                            label={t('dateOfBirth')}
                            type="date"
                            value={values.dateOfBirth}
                            onChange={e => onChange({ dateOfBirth: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />

                        <Box>
                            <TextField
                                label={t('screenName')}
                                value={values.screenName}
                                onChange={e => onChange({ screenName: e.target.value })}
                                fullWidth
                            />
                            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.75, display: 'block' }}>
                                {t('screenNameHint')}
                            </Typography>
                        </Box>
                        <Box sx={{gap: 1.5, display: 'flex', flexDirection: 'column'}}>
                            <TextField
                                label={t('address')}
                                value={values.addressLine1}
                                onChange={e => onChange({ addressLine1: e.target.value })}
                                fullWidth
                            />

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                                    <TextField
                                        label={t('city')}
                                        value={values.city}
                                        onChange={e => onChange({ city: e.target.value })}
                                        fullWidth
                                    />
                                </Box>
                                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                                    <TextField
                                        label={t('state')}
                                        value={values.state}
                                        onChange={e => onChange({ state: e.target.value })}
                                        fullWidth
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                                    <TextField
                                        label={t('postalCode')}
                                        value={values.postalCode}
                                        onChange={e => onChange({ postalCode: e.target.value })}
                                        fullWidth
                                    />
                                </Box>
                                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                                    <TextField
                                        label={t('country')}
                                        value={values.country}
                                        onChange={e => onChange({ country: e.target.value })}
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {saveError && (
                            <Alert severity="error" sx={{ borderRadius: 1 }}>
                                {saveError}
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'right', gap: 2, pt: { xs: 1, sm: 1.5 } }}>
                            <Button
                                variant="outlined"
                                onClick={onCancel}
                                disabled={isSaving}
                                sx={{ textTransform: 'none', minWidth: 140, fontWeight: 600, py: 1.5 }}
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={onSave}
                                disabled={isSaving}
                                sx={{ textTransform: 'none', minWidth: 160, fontWeight: 600, py: 1.5 }}
                            >
                                {isSaving ? <CircularProgress size={22} color="inherit" /> : t('saveChanges')}
                            </Button>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    )
}

export { EditProfile }
export type { EditProfileProps }

